import { useCallback } from 'react';

import type { FormResult } from '@sine/sine-core-api-types';

import formService from '../../../services/form/form.service';
import { useSignIn } from '../SigninProvider';
import type { PrePass, VisitorDetailsFormValues } from '../components/visitorCheckInModal.types';
import { useIdentityCheck } from './useIdentityCheck';

interface PrefillPass {
  invitation?: string;
  adminNotes?: string | null;
  visitorNotes?: string | null;
  photoURL?: string | null;
}

interface UseProcessIdentityCheckParams {
  hasCheckInForm: boolean;
  isPrefilled: boolean;
  prefillPass: PrefillPass;
  formRequired: boolean;
  onPageChange: (page: string) => void;
  onPrefilledFormChange: (form: any) => void;
  onPrePassChange: (prePass: PrePass) => void;
  PAGE: { CHECK_IN_FORM: string };
}

export function useProcessIdentityCheck({
  hasCheckInForm,
  isPrefilled,
  prefillPass,
  formRequired,
  onPageChange,
  onPrefilledFormChange,
  onPrePassChange,
  PAGE,
}: UseProcessIdentityCheckParams) {
  const { identityCheck, isLoadingIdentityCheck } = useIdentityCheck();
  const { siteRequirements, invitationWithFormResponses, zonesEnabled } = useSignIn();

  const retrievePrefilledForm = useCallback(
    async (newPass: PrePass, visitorTypeId: string) => {
      const formPrefill = await formService.retrieveFormPrefill(newPass.siteId, newPass, 'signin');

      const _prefilledForm = formService.applyPrefilledFieldByVisitorType(
        formPrefill,
        siteRequirements!.form!,
        visitorTypeId,
      );

      onPrefilledFormChange(_prefilledForm);
    },
    [siteRequirements, onPrefilledFormChange],
  );

  const processIdentityCheck = useCallback(
    async (visitorDetails: VisitorDetailsFormValues) => {
      try {
        const {
          firstName,
          lastName,
          email,
          mobile,
          company,
          companyId,
          companySource,
          host: selectedHost,
          zone: selectedZone,
          site: _selectedSite,
          visitorType: _selectedVisitorType,
        } = visitorDetails;

        const { invitation: invitationId, adminNotes, visitorNotes } = prefillPass;

        const identityToken = await identityCheck({
          firstName,
          lastName,
          email,
          mobile,
          company,
          companyId,
          companySource,
          siteId: _selectedSite!.value as string,
          zoneId: zonesEnabled && selectedZone ? (selectedZone.value as string) : undefined,
          visitorTypeId: _selectedVisitorType!.value as string,
          invitationId,
          adminNotes,
          visitorNotes,
        });

        const { photoURL } = prefillPass;

        const siteId = _selectedSite!.value as string;
        const site = { id: _selectedSite!.value as string };
        const visitorType = _selectedVisitorType!.value as string;

        const _prePass: PrePass = {
          firstName,
          lastName,
          email,
          mobile,
          visitorType,
          company,
          ...(companyId && { companyId }),
          ...(companySource && { companySource }),
          siteId,
          site,
          identityToken,
          photoURL,
        };

        if (selectedHost) {
          _prePass['host'] = { id: selectedHost.value as string };
          _prePass['hostId'] = selectedHost.value as string;
        }

        if (zonesEnabled && selectedZone) {
          _prePass['zoneId'] = selectedZone.value as string;
        }

        if (hasCheckInForm) {
          // If there are pre-registered invitation form responses, apply them first instead of retrieving the prefilled form
          if (
            invitationWithFormResponses &&
            'formResponses' in invitationWithFormResponses &&
            invitationWithFormResponses.formResponses
          ) {
            const _prefilledForm = formService.applyInvitationFormResponseByVisitorType(
              invitationWithFormResponses.formResponses as FormResult,
              siteRequirements!.form!,
              visitorType,
            );

            if ('responses' in _prefilledForm && _prefilledForm.responses.length > 0) {
              onPrefilledFormChange(_prefilledForm);
            } else {
              await retrievePrefilledForm(_prePass, visitorType);
            }
          } else if (isPrefilled) {
            await retrievePrefilledForm(_prePass, visitorType);
          }

          if (formRequired) {
            onPrePassChange(_prePass);
            onPageChange(PAGE.CHECK_IN_FORM);
          } else {
            return _prePass;
          }
        } else {
          return _prePass;
        }
      } catch (error: any) {
        if (error.code === 'CHECK_IN_NOT_PERMITTED') {
          // Error is already handled in useIdentityCheck hook
          // Re-throw to allow calling code to handle modal close
          throw error;
        }
      }
    },
    [
      prefillPass,
      identityCheck,
      hasCheckInForm,
      invitationWithFormResponses,
      isPrefilled,
      formRequired,
      siteRequirements,
      zonesEnabled,
      onPrefilledFormChange,
      retrievePrefilledForm,
      onPrePassChange,
      onPageChange,
      PAGE.CHECK_IN_FORM,
    ],
  );

  return {
    processIdentityCheck,
    isLoadingIdentityCheck,
  };
}
